/**
 * AdminRestoreSong — v3.0.151
 *
 * Admin-only utility to restore songs from SUNO back into a WiM
 * user's library. Three flows:
 *   1) Single by SUNO task_id (with preview step)
 *   2) Single by direct SUNO audio URL
 *   3) Bulk from an account backup's manifest.json
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import {
  ArrowLeft, Music, Loader2, Check, ExternalLink, AlertTriangle,
  Eye, Upload, PackageOpen, Play, Search, Copy, ArchiveRestore,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { apiFetch } from '../App';

const fmtDuration = (sec) => {
  if (!sec) return '';
  const s = Math.floor(sec);
  return `${Math.floor(s / 60)}:${(s % 60).toString().padStart(2, '0')}`;
};

// ---- Single-song form (tabs A + B share this)
function SingleSongForm() {
  const [form, setForm] = useState({
    user_email: '',
    suno_task_id: '',
    song_index: 0,
    audio_url: '',
    title: '',
    image_url: '',
    lyrics: '',
    style: '',
    charge_credits: 0,
  });
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const set = (k) => (e) => setForm((p) => ({
    ...p, [k]: e.target.type === 'number' ? Number(e.target.value) : e.target.value,
  }));

  const buildBody = () => ({
    user_email: form.user_email.trim(),
    suno_task_id: form.suno_task_id.trim() || null,
    song_index: Number(form.song_index) || 0,
    audio_url: form.audio_url.trim() || null,
    title: form.title.trim() || null,
    image_url: form.image_url.trim() || null,
    lyrics: form.lyrics.trim() || null,
    style: form.style.trim() || null,
    charge_credits: Number(form.charge_credits) || 0,
  });

  const doPreview = async () => {
    if (!form.user_email.trim()) { toast.error('User email required'); return; }
    if (!form.suno_task_id.trim() && !form.audio_url.trim()) {
      toast.error('Provide task ID or audio URL'); return;
    }
    setBusy(true); setResult(null); setPreview(null);
    try {
      const { data } = await apiFetch('/admin/songs/restore-from-suno/preview', {
        method: 'POST', body: JSON.stringify(buildBody()),
      });
      setPreview(data);
      if (data.already_restored) {
        toast.info('This clip was already restored to that account earlier.');
      }
    } catch (err) { toast.error(err.message || 'Preview failed'); }
    finally { setBusy(false); }
  };

  const doCommit = async () => {
    setBusy(true);
    try {
      const { data } = await apiFetch('/admin/songs/restore-from-suno', {
        method: 'POST', body: JSON.stringify(buildBody()),
      });
      setResult(data);
      toast.success(data.already_restored ? 'Already restored earlier' : `Restored → ${data.song?.title}`);
    } catch (err) { toast.error(err.message || 'Restore failed'); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-4">
      <div>
        <label className="block text-xs uppercase tracking-wider text-zinc-500 mb-1">
          Target user email <span className="text-rose-400">*</span>
        </label>
        <Input
          type="email"
          autoComplete="email"
          value={form.user_email}
          onChange={set('user_email')}
          placeholder="user@example.com"
          data-testid="restore-user-email"
          className="bg-black/40 border-white/10"
        />
      </div>

      <div className="border-t border-white/10 pt-4">
        <div className="text-xs uppercase tracking-wider text-emerald-300 mb-2">Option A — By SUNO task ID</div>
        <Input
          value={form.suno_task_id}
          onChange={set('suno_task_id')}
          placeholder="1a2b3c4d-5678-9abc-def0-123456789abc"
          data-testid="restore-task-id"
          className="bg-black/40 border-white/10 font-mono text-xs mb-2"
        />
        <label className="block text-xs text-zinc-500 mb-1">Song index (SUNO renders in pairs)</label>
        <select
          value={form.song_index}
          onChange={set('song_index')}
          data-testid="restore-song-index"
          className="w-full bg-black/40 border border-white/10 rounded px-2 py-1.5 text-sm"
        >
          <option value={0}>0 (first clip)</option>
          <option value={1}>1 (second clip)</option>
        </select>
      </div>

      <div className="border-t border-white/10 pt-4">
        <div className="text-xs uppercase tracking-wider text-emerald-300 mb-2">Option B — By direct audio URL</div>
        <Input
          value={form.audio_url}
          onChange={set('audio_url')}
          placeholder="https://cdn.sunoapi.org/..."
          data-testid="restore-audio-url"
          className="bg-black/40 border-white/10 text-xs"
        />
      </div>

      <details className="border-t border-white/10 pt-4">
        <summary className="text-xs uppercase tracking-wider text-zinc-500 cursor-pointer hover:text-white">
          Optional metadata overrides
        </summary>
        <div className="space-y-3 mt-3">
          <Input value={form.title} onChange={set('title')} placeholder="Title" className="bg-black/40 border-white/10" />
          <Input value={form.image_url} onChange={set('image_url')} placeholder="Image URL" className="bg-black/40 border-white/10 text-xs" />
          <Input value={form.style} onChange={set('style')} placeholder="Style / tags" className="bg-black/40 border-white/10" />
          <textarea
            value={form.lyrics}
            onChange={set('lyrics')}
            placeholder="Lyrics"
            rows={3}
            className="w-full bg-black/40 border border-white/10 rounded px-2 py-1.5 text-sm resize-y"
          />
          <div>
            <label className="block text-xs text-zinc-500 mb-1">Charge credits (0 = free)</label>
            <Input type="number" min={0} value={form.charge_credits} onChange={set('charge_credits')} className="bg-black/40 border-white/10 w-32" />
          </div>
        </div>
      </details>

      {!preview ? (
        <Button onClick={doPreview} disabled={busy} data-testid="restore-preview-btn" className="bg-emerald-500 text-black hover:bg-emerald-400 w-full">
          {busy ? <Loader2 size={16} className="animate-spin mr-2" /> : <Eye size={16} className="mr-2" />}
          {busy ? 'Fetching…' : 'Preview'}
        </Button>
      ) : (
        <div className="border border-emerald-500/30 rounded-lg p-4 bg-emerald-500/5 space-y-3" data-testid="restore-preview-card">
          <div className="text-xs uppercase tracking-wider text-emerald-300">Preview — double-check this is the right clip</div>
          <div className="flex gap-3">
            {preview.image_url ? (
              <img src={preview.image_url} alt="" className="w-24 h-24 rounded object-cover" />
            ) : (
              <div className="w-24 h-24 rounded bg-white/10 flex items-center justify-center"><Music size={28} /></div>
            )}
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-white truncate">{preview.title}</div>
              <div className="text-xs text-zinc-400 mt-0.5">
                {preview.duration ? `${fmtDuration(preview.duration)} · ` : ''}
                {preview.style || 'no style tag'}
              </div>
              {preview.audio_url && (
                <audio controls src={preview.audio_url} className="w-full mt-2 h-8" />
              )}
              {preview.lyrics_preview && (
                <details className="mt-2">
                  <summary className="text-xs text-emerald-300 cursor-pointer">Lyrics preview</summary>
                  <pre className="text-[11px] text-zinc-300 whitespace-pre-wrap mt-1 max-h-32 overflow-y-auto">{preview.lyrics_preview}</pre>
                </details>
              )}
              {preview.already_restored && (
                <div className="text-xs text-amber-300 mt-2 flex items-center gap-1">
                  <AlertTriangle size={12} /> Already restored — existing song_id {preview.existing_song?.song_id}
                </div>
              )}
            </div>
          </div>
          <div className="flex gap-2">
            <Button onClick={() => setPreview(null)} variant="outline" className="border-white/20 text-white hover:bg-white/10 flex-1">
              Back
            </Button>
            <Button onClick={doCommit} disabled={busy} data-testid="restore-commit-btn" className="bg-emerald-500 text-black hover:bg-emerald-400 flex-1">
              {busy ? <Loader2 size={16} className="animate-spin mr-2" /> : <Check size={16} className="mr-2" />}
              {busy ? 'Restoring…' : 'Confirm restore'}
            </Button>
          </div>
        </div>
      )}

      {result && (
        <div className="mt-4 p-3 bg-emerald-500/10 border border-emerald-500/20 rounded text-sm" data-testid="restore-result">
          <div className="text-emerald-300 font-semibold mb-1">
            {result.already_restored ? '↺ Already restored' : '✓ Restored'}
          </div>
          <div className="text-xs text-zinc-300">
            song_id: <span className="font-mono">{result.song?.song_id}</span> · {result.song?.title}
          </div>
        </div>
      )}
    </div>
  );
}

// ---- Bulk-from-manifest form
function BulkFromBundleForm() {
  const [email, setEmail] = useState('');
  const [manifestText, setManifestText] = useState('');
  const [zipFile, setZipFile] = useState(null);
  const [zipMode, setZipMode] = useState(false);
  const [busy, setBusy] = useState(false);
  const [dryRun, setDryRun] = useState(null);
  const [result, setResult] = useState(null);

  const readManifestFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.name.toLowerCase().endsWith('.zip')) {
      // v3.0.152 — Server-side ZIP handling. Just remember the file.
      setZipFile(file);
      setZipMode(true);
      setManifestText('');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => { setManifestText(reader.result || ''); setZipMode(false); setZipFile(null); };
    reader.readAsText(file);
  };

  const clearZip = () => { setZipFile(null); setZipMode(false); };

  const submitViaZip = async (dry) => {
    const B = process.env.REACT_APP_BACKEND_URL;
    const token = window.localStorage.getItem('wim_session_token') || '';
    const fd = new FormData();
    fd.append('user_email', email.trim());
    fd.append('file', zipFile);
    fd.append('dry_run', dry ? 'true' : 'false');
    const res = await fetch(`${B}/api/admin/songs/restore-from-zip`, {
      method: 'POST', body: fd,
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    const j = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(j?.detail || `Upload failed (${res.status})`);
    return j;
  };

  const doDryRun = async () => {
    if (!email.trim()) { toast.error('Email required'); return; }
    if (!zipMode && !manifestText.trim()) { toast.error('Paste a manifest or upload a ZIP'); return; }
    setBusy(true); setDryRun(null); setResult(null);
    try {
      const data = zipMode
        ? await submitViaZip(true)
        : (await apiFetch('/admin/songs/restore-from-bundle', {
            method: 'POST',
            body: JSON.stringify({ user_email: email.trim(), manifest_json: manifestText, dry_run: true }),
          })).data;
      setDryRun(data);
    } catch (err) { toast.error(err.message || 'Dry-run failed'); }
    finally { setBusy(false); }
  };

  const doCommit = async () => {
    setBusy(true);
    try {
      const data = zipMode
        ? await submitViaZip(false)
        : (await apiFetch('/admin/songs/restore-from-bundle', {
            method: 'POST',
            body: JSON.stringify({ user_email: email.trim(), manifest_json: manifestText }),
          })).data;
      setResult(data);
      toast.success(`Bulk restore complete — ${data.summary?.restored || 0} restored, ${data.summary?.errors || 0} errors`);
    } catch (err) { toast.error(err.message || 'Bulk restore failed'); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-4">
      <div>
        <label className="block text-xs uppercase tracking-wider text-zinc-500 mb-1">
          Target user email <span className="text-rose-400">*</span>
        </label>
        <Input type="email" autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="user@example.com" data-testid="bulk-user-email" className="bg-black/40 border-white/10" />
      </div>
      <div>
        <label className="block text-xs uppercase tracking-wider text-zinc-500 mb-1">
          Account backup — upload ZIP or paste manifest.json
        </label>
        <div className="flex items-center gap-2 mb-2">
          <label className="inline-flex items-center gap-1.5 text-xs text-emerald-300 hover:text-emerald-200 cursor-pointer">
            <Upload size={12} />
            {zipMode ? 'Replace file' : 'Upload .zip or .json'}
            <input
              type="file"
              accept=".zip,application/zip,.json,application/json"
              onChange={readManifestFile}
              className="hidden"
              data-testid="bulk-manifest-file"
            />
          </label>
          {zipMode && zipFile && (
            <span className="text-xs text-emerald-300 flex items-center gap-1.5" data-testid="bulk-zip-badge">
              📦 {zipFile.name} ({(zipFile.size / 1024 / 1024).toFixed(2)} MB)
              <button onClick={clearZip} className="text-zinc-500 hover:text-white" title="Remove">×</button>
            </span>
          )}
        </div>
        {!zipMode && (
          <textarea
            value={manifestText}
            onChange={(e) => setManifestText(e.target.value)}
            rows={8}
            placeholder='{"items": [{"type":"song","title":"...","suno_task_id":"..."}, ...]}'
            data-testid="bulk-manifest-json"
            className="w-full bg-black/40 border border-white/10 rounded px-2 py-1.5 text-xs font-mono resize-y"
          />
        )}
      </div>
      {!dryRun ? (
        <Button onClick={doDryRun} disabled={busy} data-testid="bulk-dryrun-btn" className="bg-emerald-500 text-black hover:bg-emerald-400 w-full">
          {busy ? <Loader2 size={16} className="animate-spin mr-2" /> : <Eye size={16} className="mr-2" />}
          {busy ? 'Analysing…' : 'Preview batch'}
        </Button>
      ) : (
        <div className="border border-emerald-500/30 rounded-lg p-4 bg-emerald-500/5 space-y-3">
          <div className="text-xs uppercase tracking-wider text-emerald-300">
            Batch preview — {dryRun.song_count} song{dryRun.song_count === 1 ? '' : 's'} in manifest for {dryRun.target_email}
          </div>
          <div className="max-h-64 overflow-y-auto space-y-1 border border-white/10 rounded p-2 bg-black/40">
            {(dryRun.restorable || []).map((s, i) => (
              <div key={i} className="text-xs flex items-center gap-2">
                <span className={
                  s.path === 'task_id' ? 'text-emerald-300' :
                  s.path === 'url' ? 'text-amber-300' :
                  'text-rose-400'
                }>
                  {s.path === 'unrecoverable' ? '✕' : '✓'}
                </span>
                <span className="flex-1 truncate">{s.title}</span>
                <span className="text-zinc-500">{s.path}</span>
              </div>
            ))}
          </div>
          <div className="flex gap-2">
            <Button onClick={() => setDryRun(null)} variant="outline" className="border-white/20 text-white hover:bg-white/10 flex-1">Back</Button>
            <Button onClick={doCommit} disabled={busy} data-testid="bulk-commit-btn" className="bg-emerald-500 text-black hover:bg-emerald-400 flex-1">
              {busy ? <Loader2 size={16} className="animate-spin mr-2" /> : <Check size={16} className="mr-2" />}
              {busy ? 'Restoring…' : `Restore ${dryRun.restorable?.filter((s) => s.path !== 'unrecoverable').length || 0} song(s)`}
            </Button>
          </div>
        </div>
      )}

      {result && (
        <div className="mt-2 p-3 bg-emerald-500/10 border border-emerald-500/20 rounded text-sm" data-testid="bulk-result">
          <div className="font-semibold text-emerald-300 mb-2">Batch complete</div>
          <div className="grid grid-cols-4 gap-2 text-center text-xs">
            <div><div className="text-lg font-mono text-emerald-300">{result.summary?.restored ?? 0}</div><div className="text-zinc-500">Restored</div></div>
            <div><div className="text-lg font-mono text-amber-300">{result.summary?.already_restored ?? 0}</div><div className="text-zinc-500">Already</div></div>
            <div><div className="text-lg font-mono text-zinc-400">{result.summary?.skipped ?? 0}</div><div className="text-zinc-500">Skipped</div></div>
            <div><div className="text-lg font-mono text-rose-400">{result.summary?.errors ?? 0}</div><div className="text-zinc-500">Errors</div></div>
          </div>
          <details className="mt-2">
            <summary className="text-xs text-emerald-300 cursor-pointer">Per-song details</summary>
            <div className="mt-2 max-h-48 overflow-y-auto space-y-0.5 text-[11px] font-mono">
              {result.results?.map((r, i) => (
                <div key={i} className="flex items-center gap-2">
                  <span className={
                    r.status === 'restored' ? 'text-emerald-300' :
                    r.status === 'already_restored' ? 'text-amber-300' :
                    r.status === 'skipped' ? 'text-zinc-500' :
                    'text-rose-400'
                  }>{r.status}</span>
                  <span className="flex-1 truncate">{r.title}</span>
                  {r.reason && <span className="text-zinc-500 truncate">{r.reason}</span>}
                </div>
              ))}
            </div>
          </details>
        </div>
      )}
    </div>
  );
}

// ---- v3.0.153 — Search the SUNO webhook log by title / user / date.
// Every SUNO callback fires a hit into `db.suno_webhook_log`, which we
// enriched to store per-item title / style / prompt / audio_url. This
// lets an admin recover a lost song's task_id without knowing it upfront:
// they type the title, we surface every matching task_id + a Restore link.
function WebhookLogSearch() {
  const [form, setForm] = useState({ q: '', user_email: '', start: '', end: '' });
  const [busy, setBusy] = useState(false);
  const [hooks, setHooks] = useState(null);
  const set = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  const doSearch = async () => {
    setBusy(true); setHooks(null);
    try {
      const params = new URLSearchParams();
      if (form.q.trim()) params.set('q', form.q.trim());
      if (form.user_email.trim()) params.set('user_email', form.user_email.trim());
      if (form.start) params.set('start', form.start);
      if (form.end) params.set('end', form.end);
      params.set('limit', '50');
      const { data } = await apiFetch(`/admin/suno-webhooks/search?${params.toString()}`);
      setHooks(data);
    } catch (err) { toast.error(err.message || 'Search failed'); }
    finally { setBusy(false); }
  };

  const copyTaskId = (tid) => {
    if (!tid) return;
    navigator.clipboard?.writeText(tid);
    toast.success('task_id copied');
  };

  return (
    <div className="space-y-4">
      <div className="text-xs text-zinc-500 -mt-2">
        Search the SUNO webhook log. Rows written after v3.0.153 include song
        title, style, prompt, and audio URL — use title to find a lost song's
        task_id, then click <b>Restore</b>.
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <Input
          placeholder="Title, style, or prompt (e.g. Dog Days)"
          value={form.q}
          onChange={set('q')}
          className="bg-black/40 border-white/10"
          data-testid="webhook-search-q"
          onKeyDown={(e) => e.key === 'Enter' && doSearch()}
        />
        <Input
          type="email"
          autoComplete="email"
          placeholder="User email (optional)"
          value={form.user_email}
          onChange={set('user_email')}
          className="bg-black/40 border-white/10"
          data-testid="webhook-search-user"
          onKeyDown={(e) => e.key === 'Enter' && doSearch()}
        />
        <div>
          <label className="block text-[10px] uppercase tracking-wider text-zinc-500 mb-1">From (ISO)</label>
          <Input
            placeholder="2026-08-01"
            value={form.start}
            onChange={set('start')}
            className="bg-black/40 border-white/10"
            data-testid="webhook-search-start"
          />
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-wider text-zinc-500 mb-1">To (ISO)</label>
          <Input
            placeholder="2026-09-07"
            value={form.end}
            onChange={set('end')}
            className="bg-black/40 border-white/10"
            data-testid="webhook-search-end"
          />
        </div>
      </div>

      <Button
        onClick={doSearch}
        disabled={busy}
        className="bg-emerald-500 text-black hover:bg-emerald-400 w-full"
        data-testid="webhook-search-submit"
      >
        {busy ? <Loader2 size={14} className="animate-spin mr-1" /> : <Search size={14} className="mr-1" />}
        Search webhook log
      </Button>

      {hooks && (
        <div className="mt-4">
          <div className="text-xs text-zinc-500 mb-2">
            {hooks.showing} match{hooks.showing === 1 ? '' : 'es'}
            {hooks.note ? ` — ${hooks.note}` : ''}
          </div>
          {hooks.showing === 0 ? (
            <div className="text-center py-8 border border-dashed border-white/10 rounded-md text-zinc-500 text-sm">
              Nothing found. Try broader search terms — enriched fields only exist for hooks received after v3.0.153.
            </div>
          ) : (
            <div className="border border-white/10 rounded-md divide-y divide-white/5">
              {hooks.hooks.map((h) => {
                const item = (h.items && h.items[0]) || {};
                const cur = h.current_song || {};
                return (
                  <div
                    key={h.task_id}
                    className="p-3 flex items-start gap-3"
                    data-testid={`webhook-row-${h.task_id}`}
                  >
                    {item.image_url ? (
                      <img src={item.image_url} alt="cover" className="w-12 h-12 rounded object-cover shrink-0" />
                    ) : (
                      <div className="w-12 h-12 rounded bg-white/5 shrink-0" />
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-white truncate">
                        {item.title || cur.title || <span className="text-zinc-500 italic">no title captured</span>}
                        {cur.hard_deleted && (
                          <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-rose-500/20 text-rose-300 font-mono">HARD-DELETED</span>
                        )}
                        {cur.in_trash && (
                          <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-200 font-mono">IN TRASH</span>
                        )}
                        {!cur.hard_deleted && !cur.in_trash && cur.title && (
                          <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-300 font-mono">LIVE</span>
                        )}
                      </div>
                      <div className="text-xs text-zinc-500 mt-1 flex flex-wrap items-center gap-2">
                        <span>{new Date(h.received_at).toLocaleString()}</span>
                        <span className="text-zinc-600">·</span>
                        <span title={h.user_id || ''}>{h.user_email || h.user_id || 'unknown user'}</span>
                        {item.duration && <><span className="text-zinc-600">·</span><span>{fmtDuration(item.duration)}</span></>}
                      </div>
                      <div className="text-[11px] font-mono text-zinc-500 mt-1 flex items-center gap-1.5">
                        <span className="text-zinc-600">task:</span>
                        <span className="text-emerald-300">{h.task_id}</span>
                        <button
                          onClick={() => copyTaskId(h.task_id)}
                          className="text-zinc-500 hover:text-white"
                          title="Copy task_id"
                          data-testid={`webhook-copy-${h.task_id}`}
                        >
                          <Copy size={12} />
                        </button>
                        {item.audio_url && (
                          <a
                            href={item.audio_url}
                            target="_blank"
                            rel="noreferrer"
                            className="text-zinc-500 hover:text-white ml-1"
                            title="Open source audio"
                          >
                            <ExternalLink size={12} />
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ---- Main page with tabs
export default function AdminRestoreSong() {
  const [tab, setTab] = useState('single');
  return (
    <div className="min-h-screen bg-gradient-to-b from-zinc-950 to-black text-white pb-24">
      <div className="max-w-2xl mx-auto px-4 py-6">
        <Link to="/admin" className="inline-flex items-center gap-1.5 text-sm text-zinc-400 hover:text-white mb-6">
          <ArrowLeft size={14} /> Back to Admin
        </Link>

        <div className="flex items-center gap-3 mb-4">
          <div className="p-3 bg-emerald-500/10 rounded-lg">
            <Music className="text-emerald-300" size={24} />
          </div>
          <div>
            <h1 className="text-2xl font-bold">Restore songs</h1>
            <p className="text-sm text-zinc-400">Re-import missing songs from SUNO or an account backup.</p>
          </div>
        </div>

        <div className="flex gap-2 mb-6 border-b border-white/10 overflow-x-auto">
          <button
            onClick={() => setTab('single')}
            data-testid="restore-tab-single"
            className={`px-3 py-2 text-sm flex items-center gap-1.5 border-b-2 whitespace-nowrap ${tab === 'single' ? 'border-emerald-400 text-white' : 'border-transparent text-zinc-500 hover:text-white'}`}
          >
            <Play size={14} /> Single song
          </button>
          <button
            onClick={() => setTab('bulk')}
            data-testid="restore-tab-bulk"
            className={`px-3 py-2 text-sm flex items-center gap-1.5 border-b-2 whitespace-nowrap ${tab === 'bulk' ? 'border-emerald-400 text-white' : 'border-transparent text-zinc-500 hover:text-white'}`}
          >
            <PackageOpen size={14} /> Bulk from backup
          </button>
          <button
            onClick={() => setTab('search')}
            data-testid="restore-tab-search"
            className={`px-3 py-2 text-sm flex items-center gap-1.5 border-b-2 whitespace-nowrap ${tab === 'search' ? 'border-emerald-400 text-white' : 'border-transparent text-zinc-500 hover:text-white'}`}
          >
            <ArchiveRestore size={14} /> Search webhook log
          </button>
        </div>

        {tab === 'single' && <SingleSongForm />}
        {tab === 'bulk' && <BulkFromBundleForm />}
        {tab === 'search' && <WebhookLogSearch />}
      </div>
    </div>
  );
}
